/**
 * Reading ANY file the purchaser uploads — the layer above `readUploadedFile.ts`.
 *
 * The local readers (workbook, Word, PDF text layer) run first and are the answer for every file
 * that carries its own structure. Only what they cannot see goes to Azure Document Intelligence
 * (`documentIntelligence.ts`): an image, or a PDF whose text layer is empty — a scan. The OCR's
 * tables then go through the SAME workbook reader, and a page of prose through the SAME LLM
 * mail-body reader, so nothing downstream learns a new shape (Akhil, 2026-09-17: "image is only
 * read is above layer — the one we already have not needed to change").
 *
 * `vision` on the result says which path answered, for the toast: a figure read off a photo is
 * worth a second look in a way a spreadsheet cell is not.
 */

import { sniffFileKind } from './fileKind'
import { analyzeLayout, diSheets, diText, visionAvailable, type DiAnalyzeResult } from './documentIntelligence'
import { readMailItems, type ReadItemsResult } from './readMailItems'
import { readUploadedFile } from './readUploadedFile'
import { extractLinesFromBody, mailReaderAvailable } from './mailBodyExtract'
import type { SheetItem } from './sheetExtract'

export interface VisionDeps {
  readLocally: (name: string, bytes: Uint8Array) => Promise<ReadItemsResult>
  analyze: (bytes: Uint8Array) => Promise<DiAnalyzeResult>
  available: () => Promise<boolean>
  readProse: (text: string) => Promise<SheetItem[]>
}

export interface DocumentReadResult extends ReadItemsResult {
  /** `tables` — the OCR found a table; `text` — prose, read by the LLM. Absent: the local readers. */
  vision?: 'tables' | 'text'
}

/** How many bytes the sniffer needs — HEIC's `ftyp` box sits at offset 4. */
const HEAD = 12

const usable = (r: ReadItemsResult) => r.items.some((i) => i.description.trim() !== '' && i.qty > 0)

const LIVE_DEPS: VisionDeps = {
  readLocally: (name, bytes) => readUploadedFile(new File([bytes as BlobPart], name)),
  analyze: (bytes) => analyzeLayout(bytes),
  available: visionAvailable,
  readProse: async (text) => ((await mailReaderAvailable()) ? extractLinesFromBody(text) : []),
}

export async function readDocument(name: string, bytes: Uint8Array, deps: VisionDeps = LIVE_DEPS): Promise<DocumentReadResult> {
  const kind = sniffFileKind(bytes.subarray(0, HEAD))

  if (kind !== 'image') {
    const local = await deps.readLocally(name, bytes)
    // A PDF the text reader found nothing in is a scan — the one non-image case OCR rescues.
    if (kind !== 'pdf' || usable(local)) return local
  }

  if (!(await deps.available())) {
    return {
      items: [],
      readable: false,
      source: null,
      skipped: [{ name, reason: `${kind === 'image' ? 'images' : 'scanned PDFs'} are not read on this deployment (no Document Intelligence configured)` }],
    }
  }

  const result = await deps.analyze(bytes)
  const sheets = diSheets(result)
  if (sheets.length > 0) {
    const fromTables = readMailItems('', [{ name, kind: 'xlsx', sheets }])
    if (usable(fromTables)) return { ...fromTables, vision: 'tables' }
  }
  const text = diText(result).trim()
  if (text !== '') {
    const items = await deps.readProse(text)
    if (items.length > 0) return { items, readable: true, source: 'pdf', skipped: [], vision: 'text' }
  }
  return {
    items: [],
    readable: false,
    source: null,
    skipped: [{ name, reason: text === '' ? 'no text or table could be made out' : 'no item lines could be made out of the text' }],
  }
}
